import { useEffect, useState } from "react";
import { useUIStore } from "../store/ui";
import { DEFAULT_THEME_ID } from "@caliu/styles/themes";
import { setProfileProps } from "../lib/analytics";

/**
 * Paints the browser's own chrome (Safari's top and bottom bars, the Android
 * status bar) with the page background, so it reads as part of the content.
 * Reads the computed `<html>` background rather than a theme token because the
 * colour depends on the screen too (`html[data-screen]` in index.css). Safari
 * 26 ignores theme-color and samples that same background, so both agree.
 */
export function syncBrowserChromeColor() {
  const color = getComputedStyle(document.documentElement).backgroundColor;
  document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((el) => {
      el.setAttribute("content", color);
    });
}

/**
 * Applies theme preferences to the <html> element:
 *
 * 1. Light/dark mode via .light / .dark class
 * 2. Color theme via data-theme="<id>" attribute
 *
 * The user can pick a different color theme per mode (day/night pairing), so
 * the mode class is always applied explicitly — even in "system" mode, which
 * is resolved via matchMedia and tracked reactively. Leaving <html> classless
 * (the pre-pairing behavior) would let the CSS `@media` fallback paint the
 * dark palette of whichever single data-theme is set, which is wrong the
 * moment light and dark themes differ.
 *
 * Fonts are self-hosted via @fontsource (imported in src/lib/fonts.ts),
 * so no dynamic Google Fonts loading is needed.
 */
export function useTheme() {
  const { theme, colorThemeLight, colorThemeDark } = useUIStore();

  // Track the OS preference reactively so "system" mode follows live changes.
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia("(prefers-color-scheme: dark)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const isDark = theme === "dark" || (theme === "system" && systemDark);
  const colorTheme = isDark ? colorThemeDark : colorThemeLight;

  // Apply light/dark mode class + the matching mode's color theme
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add("dark");
      root.classList.remove("light");
    } else {
      root.classList.add("light");
      root.classList.remove("dark");
    }

    if (colorTheme === DEFAULT_THEME_ID) {
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", colorTheme);
    }

    syncBrowserChromeColor();
  }, [isDark, colorTheme]);

  // Keep the profile's theme/mode segmentation attributes current (idempotent,
  // safe to run on hydration — it just mirrors the active state).
  useEffect(() => {
    setProfileProps({ theme: colorTheme, color_mode: theme });
  }, [theme, colorTheme]);

  return { theme, colorTheme, colorThemeLight, colorThemeDark, isDark };
}
